import React, { useEffect, useMemo, useState, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from 'apollo-react/components/Card';
import CardContent from 'apollo-react/components/CardContent';
import CardHeader from 'apollo-react/components/CardHeader';
import LineChart from 'apollo-react/components/LineChart';
import { green, orange, red } from 'apollo-react/colors';
import ClusterColumnChart from 'apollo-react/components/ClusterColumnChart';
import BarChart from 'apollo-react/components/BarChart';
import DonutChart from 'apollo-react/components/DonutChart';
import ListChart from 'apollo-react/components/ListChart';
import axiosInstance from '../../axiosInstance';
import {
  buildDashboardDateQueryParams,
  dashboardDateFilterKey,
} from '../../utils/dashboardDateRangeUtils';
import {
  EMPTY_CLUSTER_COLUMN_CHART_DATA,
  EMPTY_LINE_CHART_DATA,
  EMPTY_PAYMENT_CLUSTER_COLUMN_CHART_DATA,
  EMPTY_PAYMENT_LINE_CHART_DATA,
  HOME_CLUSTER_COLUMN_CHART_DATA,
  HOME_LINE_CHART_DATA,
  HOME_PAYMENT_CLUSTER_COLUMN_CHART_DATA,
  HOME_PAYMENT_LINE_CHART_CURRENCY,
  HOME_PAYMENT_LINE_CHART_DATA,
} from './homeDashboardStaticData';
import styles from './HomeDashboardCharts.module.css';

export const homePageGridItemStyle = {
  width: '100%',
  minWidth: 0,
  margin: 0,
  padding: 0,
  border: '0px',
  boxSizing: 'border-box',
};

const CHART_ROW_HEIGHT = 380;
const BLUEPRINT_CHART_HEIGHT = 280;

/** Draw Succeeded last so its line stays visible when values match Failed. */
const PAYMENT_LINE_SERIES_ORDER = ['Failed', 'Pending', 'Succeeded'];
const PAYMENT_LINE_CHART_COLORS = [red, orange, green];

function sortPaymentLineSeries(seriesList) {
  if (!Array.isArray(seriesList) || seriesList.length === 0) return seriesList;
  const byLabel = new Map(seriesList.map((series) => [series.label, series]));
  const ordered = PAYMENT_LINE_SERIES_ORDER.map((label) =>
    byLabel.get(label)
  ).filter(Boolean);
  return ordered.length > 0 ? ordered : seriesList;
}

const slaListColumns = [
  { header: 'Request ID', accessor: 'requestId' },
  { header: 'Workflow', accessor: 'workflow' },
  { header: 'Days left', accessor: 'daysLeft', align: 'right' },
  { header: 'Priority', accessor: 'priority' },
];

function isValidLineChartData(data) {
  if (!Array.isArray(data) || data.length === 0) return false;
  const pointCount = data[0]?.data?.length;
  if (!pointCount) return false;

  const referenceXValues = data[0].data.map((point) => String(point?.x));
  if (referenceXValues.some((x) => x === 'undefined' || x === 'null')) {
    return false;
  }

  return data.every(
    (series) =>
      series?.label &&
      Array.isArray(series.data) &&
      series.data.length === pointCount &&
      series.data.every(
        (point, index) =>
          point?.x != null &&
          String(point.x) === referenceXValues[index] &&
          Number.isFinite(Number(point.y))
      )
  );
}

function isValidClusterChartData(data) {
  return (
    Array.isArray(data) &&
    data.length > 0 &&
    data.every(
      (row) =>
        row?.label &&
        Array.isArray(row.data) &&
        row.data.length > 0 &&
        typeof row.data[0] === 'object'
    )
  );
}

/**
 * Apollo d3 charts attach mouse handlers in componentDidMount. Defer mount until
 * layout is ready and data is final to avoid hover on detached SVG nodes.
 */
function DeferredChartMount({
  children,
  minHeight = 280,
  mountClassName = '',
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setMounted(true));
    });
    return () => {
      window.cancelAnimationFrame(frame);
      setMounted(false);
    };
  }, []);

  if (!mounted) {
    return (
      <div
        className={styles.chartPlaceholder}
        style={{ minHeight, height: minHeight }}
        aria-hidden="true"
      />
    );
  }

  return (
    <div
      className={`${styles.chartMount} ${mountClassName}`.trim()}
      style={{ minHeight, height: minHeight }}
    >
      {children}
    </div>
  );
}

/** Apollo BarChart requires at least one row; crashes on empty `data`. */
function normalizeBarChartData(rows, fallbackLabels) {
  if (Array.isArray(rows) && rows.length > 0) {
    return rows.map((row) => ({
      label: String(row.label ?? '—'),
      value: Number(row.value) || 0,
    }));
  }
  return fallbackLabels.map((label) => ({ label, value: 0 }));
}

function totalAtLineIndex(seriesList, i) {
  let sum = 0;
  for (const s of seriesList) {
    const y = Number(s?.data?.[i]?.y);
    sum += Number.isFinite(y) ? y : 0;
  }
  return sum;
}

function filterLineSeriesNoAllZeroMonths(seriesList) {
  if (!Array.isArray(seriesList) || seriesList.length === 0) return seriesList;
  const first = seriesList[0]?.data;
  if (!Array.isArray(first) || first.length === 0) return seriesList;
  const n = first.length;
  const keepIdx = [];
  for (let i = 0; i < n; i += 1) {
    const sum = totalAtLineIndex(seriesList, i);
    if (sum > 0) {
      keepIdx.push(i);
      continue;
    }
    if (i < n - 1 && totalAtLineIndex(seriesList, i + 1) > 0) {
      keepIdx.push(i);
    }
  }
  if (keepIdx.length === 0) {
    const last = n - 1;
    return seriesList.map((s) => ({
      ...s,
      data: s.data?.length ? [s.data[last]] : [],
    }));
  }
  return seriesList.map((s) => ({
    ...s,
    data: keepIdx.map((idx) => s.data[idx]),
  }));
}

function clusterRowTotal(row) {
  const block = row?.data?.[0];
  if (!block || typeof block !== 'object') return 0;
  return Object.values(block).reduce((acc, v) => acc + (Number(v) || 0), 0);
}

function filterClusterNoAllZeroBars(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const totals = rows.map(clusterRowTotal);
  const filtered = rows.filter((_, j) => {
    if (totals[j] > 0) return true;
    if (j >= rows.length - 1) return false;
    return totals[j + 1] > 0;
  });
  if (filtered.length > 0) return filtered;
  return rows.length ? [rows[rows.length - 1]] : [];
}

/**
 * @param {{
 *   persona: 'admin' | 'super-admin' | 'customer',
 *   dateFilter?: { preset: string, customRange: any },
 *   statusBarData?: Array<{ label: string, value: number }>,
 *   priorityBarData?: Array<{ label: string, value: number }>,
 *   approvalRatePercent?: number | null,
 *   slaAtRiskRows?: Array<object>,
 *   showBlueprintCharts?: boolean,
 *   showPaymentCharts?: boolean,
 *   summaryReady?: boolean,
 * }} props
 */
export function HomeDashboardChartsSection({
  persona,
  dateFilter,
  statusBarData = [],
  priorityBarData = [],
  approvalRatePercent = null,
  slaAtRiskRows = [],
  showBlueprintCharts = true,
  showPaymentCharts = false,
  summaryReady = true,
}) {
  const navigate = useNavigate();
  const chartInstanceId = useId();
  const [chartsReady, setChartsReady] = useState(false);
  const [lineData, setLineData] = useState(HOME_LINE_CHART_DATA);
  const [clusterData, setClusterData] = useState(
    HOME_CLUSTER_COLUMN_CHART_DATA
  );
  const [paymentLineData, setPaymentLineData] = useState(
    HOME_PAYMENT_LINE_CHART_DATA
  );
  const [paymentLineCurrency, setPaymentLineCurrency] = useState(
    HOME_PAYMENT_LINE_CHART_CURRENCY
  );
  const [paymentClusterData, setPaymentClusterData] = useState(
    HOME_PAYMENT_CLUSTER_COLUMN_CHART_DATA
  );
  const [paymentChartsEnabled, setPaymentChartsEnabled] =
    useState(showPaymentCharts);

  const lineDisplay = useMemo(() => {
    const filtered = filterLineSeriesNoAllZeroMonths(lineData);
    return isValidLineChartData(filtered) ? filtered : HOME_LINE_CHART_DATA;
  }, [lineData]);

  const clusterDisplay = useMemo(() => {
    const filtered = filterClusterNoAllZeroBars(clusterData);
    if (!isValidClusterChartData(filtered) || filtered.length === 0) {
      return HOME_CLUSTER_COLUMN_CHART_DATA;
    }
    return filtered;
  }, [clusterData]);

  const paymentLineDisplay = useMemo(() => {
    const filtered = filterLineSeriesNoAllZeroMonths(paymentLineData);
    const candidate = isValidLineChartData(filtered)
      ? filtered
      : HOME_PAYMENT_LINE_CHART_DATA;
    return sortPaymentLineSeries(candidate);
  }, [paymentLineData]);

  const paymentClusterDisplay = useMemo(() => {
    const filtered = filterClusterNoAllZeroBars(paymentClusterData);
    if (!isValidClusterChartData(filtered) || filtered.length === 0) {
      return HOME_PAYMENT_CLUSTER_COLUMN_CHART_DATA;
    }
    return filtered;
  }, [paymentClusterData]);

  const dateFilterKey = useMemo(
    () => dashboardDateFilterKey(dateFilter),
    [dateFilter]
  );

  const slaRows = useMemo(
    () =>
      slaAtRiskRows.map((row) => ({
        requestId: row.requestId,
        workflow: row.workflow,
        daysLeft: row.daysLeft,
        priority: row.priority,
        onClick: row.path ? () => navigate(row.path) : undefined,
      })),
    [slaAtRiskRows, navigate]
  );

  const statusBarDisplay = useMemo(
    () =>
      normalizeBarChartData(statusBarData, [
        'Assigned',
        'In progress',
        'Approved',
        'Rejected',
      ]),
    [statusBarData]
  );

  const priorityBarDisplay = useMemo(
    () =>
      normalizeBarChartData(priorityBarData, [
        'Critical',
        'High',
        'Medium',
        'Low',
      ]),
    [priorityBarData]
  );

  useEffect(() => {
    const isCustomer = persona === 'customer';
    const url = isCustomer
      ? '/api/dashboard/home-charts/enduser'
      : '/api/dashboard/home-charts';
    const fallbackLine = isCustomer
      ? EMPTY_LINE_CHART_DATA
      : HOME_LINE_CHART_DATA;
    const fallbackCluster = isCustomer
      ? EMPTY_CLUSTER_COLUMN_CHART_DATA
      : HOME_CLUSTER_COLUMN_CHART_DATA;
    let cancelled = false;
    setChartsReady(false);

    const params = buildDashboardDateQueryParams(dateFilter);

    axiosInstance
      .get(url, { params, withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        if (isValidLineChartData(res.data?.lineChartData)) {
          setLineData(res.data.lineChartData);
        } else {
          setLineData(fallbackLine);
        }
        if (isValidClusterChartData(res.data?.clusterChartData)) {
          setClusterData(res.data.clusterChartData);
        } else {
          setClusterData(fallbackCluster);
        }
        setPaymentChartsEnabled(res.data?.showPaymentCharts === true);
        if (res.data?.showPaymentCharts === true) {
          if (isValidLineChartData(res.data?.paymentLineChartData)) {
            setPaymentLineData(res.data.paymentLineChartData);
            setPaymentLineCurrency(
              res.data?.paymentLineCurrency || HOME_PAYMENT_LINE_CHART_CURRENCY
            );
          } else {
            setPaymentLineData(EMPTY_PAYMENT_LINE_CHART_DATA);
            setPaymentLineCurrency(HOME_PAYMENT_LINE_CHART_CURRENCY);
          }
          if (isValidClusterChartData(res.data?.paymentClusterChartData)) {
            setPaymentClusterData(res.data.paymentClusterChartData);
          } else {
            setPaymentClusterData(EMPTY_PAYMENT_CLUSTER_COLUMN_CHART_DATA);
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        setLineData(fallbackLine);
        setClusterData(fallbackCluster);
        setPaymentChartsEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setChartsReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [persona, dateFilterKey, dateFilter]);

  return (
    <div className={styles.chartsSection}>
      <div className={styles.primaryChartsRow}>
        <div className={styles.chartPanel}>
          <div className={styles.chartPanelInner}>
            <Card tabIndex={0} className={styles.chartCard}>
              <CardHeader title="Requests & review throughput" />
              <CardContent
                style={{
                  flex: 1,
                  width: '100%',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {chartsReady ? (
                  <DeferredChartMount minHeight={CHART_ROW_HEIGHT}>
                    <LineChart
                      key={`${chartInstanceId}-line-${persona}`}
                      data={lineDisplay}
                      xTicks={5}
                      height={CHART_ROW_HEIGHT}
                    />
                  </DeferredChartMount>
                ) : (
                  <div
                    className={styles.chartPlaceholder}
                    style={{
                      minHeight: CHART_ROW_HEIGHT,
                      height: CHART_ROW_HEIGHT,
                    }}
                    aria-hidden="true"
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className={styles.chartPanel}>
          <div className={styles.chartPanelInner}>
            <Card tabIndex={0} className={styles.chartCard}>
              <CardHeader title="Monthly volume — submissions vs completed" />
              <CardContent
                style={{
                  flex: 1,
                  width: '100%',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {chartsReady ? (
                  <DeferredChartMount minHeight={CHART_ROW_HEIGHT}>
                    <ClusterColumnChart
                      key={`${chartInstanceId}-cluster-${persona}`}
                      data={clusterDisplay}
                      yTicks={6}
                      height={CHART_ROW_HEIGHT}
                    />
                  </DeferredChartMount>
                ) : (
                  <div
                    className={styles.chartPlaceholder}
                    style={{
                      minHeight: CHART_ROW_HEIGHT,
                      height: CHART_ROW_HEIGHT,
                    }}
                    aria-hidden="true"
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {paymentChartsEnabled && showPaymentCharts ? (
        <div className={styles.primaryChartsRow}>
          <div className={styles.chartPanel}>
            <div className={styles.chartPanelInner}>
              <Card tabIndex={0} className={styles.chartCard}>
                <CardHeader title="Payment transaction costs over time" />
                <CardContent
                  style={{
                    flex: 1,
                    width: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {chartsReady ? (
                    <DeferredChartMount minHeight={CHART_ROW_HEIGHT}>
                      <LineChart
                        key={`${chartInstanceId}-payment-line-${persona}`}
                        data={paymentLineDisplay}
                        dataColors={PAYMENT_LINE_CHART_COLORS}
                        suffix={` ${paymentLineCurrency}`}
                        xTicks={5}
                        height={CHART_ROW_HEIGHT}
                      />
                    </DeferredChartMount>
                  ) : (
                    <div
                      className={styles.chartPlaceholder}
                      style={{
                        minHeight: CHART_ROW_HEIGHT,
                        height: CHART_ROW_HEIGHT,
                      }}
                      aria-hidden="true"
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          <div className={styles.chartPanel}>
            <div className={styles.chartPanelInner}>
              <Card tabIndex={0} className={styles.chartCard}>
                <CardHeader title="Monthly volume — succeeded vs failed payments" />
                <CardContent
                  style={{
                    flex: 1,
                    width: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {chartsReady ? (
                    <DeferredChartMount minHeight={CHART_ROW_HEIGHT}>
                      <ClusterColumnChart
                        key={`${chartInstanceId}-payment-cluster-${persona}`}
                        data={paymentClusterDisplay}
                        yTicks={6}
                        height={CHART_ROW_HEIGHT}
                      />
                    </DeferredChartMount>
                  ) : (
                    <div
                      className={styles.chartPlaceholder}
                      style={{
                        minHeight: CHART_ROW_HEIGHT,
                        height: CHART_ROW_HEIGHT,
                      }}
                      aria-hidden="true"
                    />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : null}

      {showBlueprintCharts && summaryReady ? (
        <div className={styles.blueprintGrid}>
          <div className={styles.blueprintPanel}>
            <div
              className={styles.blueprintPanelInner}
              style={homePageGridItemStyle}
            >
              <Card tabIndex={0} className={styles.blueprintCard}>
                <CardHeader title="Request status distribution" />
                <CardContent className={styles.blueprintCardContent}>
                  <DeferredChartMount minHeight={BLUEPRINT_CHART_HEIGHT}>
                    <BarChart
                      key={`${chartInstanceId}-status-bar-${persona}`}
                      data={statusBarDisplay}
                      height={BLUEPRINT_CHART_HEIGHT}
                    />
                  </DeferredChartMount>
                </CardContent>
              </Card>
            </div>
          </div>
          <div className={styles.blueprintPanel}>
            <div
              className={styles.blueprintPanelInner}
              style={homePageGridItemStyle}
            >
              <Card tabIndex={0} className={styles.blueprintCard}>
                <CardHeader title="Approval rate" />
                <CardContent
                  className={`${styles.blueprintCardContent} ${styles.blueprintCardContentCenter}`}
                >
                  <DeferredChartMount
                    minHeight={BLUEPRINT_CHART_HEIGHT}
                    mountClassName={styles.approvalDonutMount}
                  >
                    <DonutChart
                      className={styles.approvalDonutChart}
                      percent={approvalRatePercent ?? 0}
                      subtitle="Approved"
                      tooltipTitle="Approval rate"
                      tooltipSubtitle={
                        approvalRatePercent != null
                          ? `${approvalRatePercent}%`
                          : 'No data yet'
                      }
                    />
                  </DeferredChartMount>
                </CardContent>
              </Card>
            </div>
          </div>
          <div className={styles.blueprintPanel}>
            <div
              className={styles.blueprintPanelInner}
              style={homePageGridItemStyle}
            >
              <Card tabIndex={0} className={styles.blueprintCard}>
                <CardHeader title="SLA priority breakdown" />
                <CardContent className={styles.blueprintCardContent}>
                  <DeferredChartMount minHeight={BLUEPRINT_CHART_HEIGHT}>
                    <BarChart
                      key={`${chartInstanceId}-priority-bar-${persona}`}
                      data={priorityBarDisplay}
                      height={BLUEPRINT_CHART_HEIGHT}
                    />
                  </DeferredChartMount>
                </CardContent>
              </Card>
            </div>
          </div>
          <div
            className={`${styles.blueprintPanel} ${styles.blueprintFullWidth}`}
          >
            <div style={homePageGridItemStyle}>
              <Card tabIndex={0}>
                <CardHeader title="SLA at risk & overdue" />
                <CardContent style={{ paddingTop: 0 }}>
                  <ListChart
                    columns={slaListColumns}
                    rows={slaRows}
                    height={BLUEPRINT_CHART_HEIGHT}
                    emptyMessage="No requests at SLA risk."
                    stripedRows
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
